import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { LanguageSelector } from '@/components/LanguageSelector';
import { getErrorMessage } from '@/lib/error-utils';

export default function ConfirmSignup() {
  const { t } = useTranslation(['auth']);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // The link is dead -- the working exit is a fresh one, sent by Supabase's
  // own confirmation rail, not a login the account can't pass (P-04).
  const [resendEmail, setResendEmail] = useState('');
  const [resendSending, setResendSending] = useState(false);
  const [resendSent, setResendSent] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);

  const handleResend = async (e: React.FormEvent) => {
    e.preventDefault();
    setResendSending(true);
    setResendError(null);
    try {
      const { error } = await supabase.auth.resend({ type: 'signup', email: resendEmail });
      if (error) {
        setResendError(error.message);
      } else {
        setResendSent(true);
      }
    } catch (err: unknown) {
      setResendError(getErrorMessage(err) || t('auth:unexpectedError'));
    } finally {
      setResendSending(false);
    }
  };

  const confirmEmail = useCallback(async () => {
    try {
      const { data, error } = await supabase.functions.invoke('confirm-signup', {
        body: { token },
      });

      if (error) {
        throw new Error(error.message || t('auth:confirmationFailed'));
      }

      if (!data?.success) {
        throw new Error(data?.error || t('auth:confirmationFailed'));
      }

      setStatus('success');

      // Redirect to dashboard after a short delay
      setTimeout(() => {
        navigate('/dashboard');
      }, 3000);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : t('auth:confirmationFailed');
      console.error('Email confirmation error:', err);
      setStatus('error');
      setErrorMessage(message);
    }
  }, [token, t, navigate]);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMessage(t('auth:invalidConfirmationLink'));
      return;
    }

    confirmEmail();
  }, [token, t, confirmEmail]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="absolute top-4 right-4">
          <LanguageSelector />
        </div>
        <Card className="w-full max-w-md">
          <CardContent className="flex items-center justify-center p-6">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            <span>{t('auth:confirmingEmail')}</span>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="absolute top-4 right-4">
          <LanguageSelector />
        </div>
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <XCircle className="h-12 w-12 text-destructive mx-auto mb-2" />
            <CardTitle>{t('auth:confirmationErrorTitle')}</CardTitle>
            <CardDescription>{errorMessage}</CardDescription>
          </CardHeader>
          <CardContent>
            {resendSent ? (
              <Alert className="border-green-500 bg-green-50 dark:border-green-700 dark:bg-green-950">
                <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                <AlertDescription className="text-green-800 dark:text-green-200">
                  {t('auth:resendConfirmationSent')}
                </AlertDescription>
              </Alert>
            ) : (
              <div className="space-y-4">
                {resendError && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{resendError}</AlertDescription>
                  </Alert>
                )}
                <form onSubmit={handleResend} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="resend-email">{t('auth:email')}</Label>
                    <Input
                      id="resend-email"
                      type="email"
                      placeholder={t('auth:emailPlaceholder')}
                      value={resendEmail}
                      onChange={(e) => setResendEmail(e.target.value)}
                      required
                      disabled={resendSending}
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={resendSending || !resendEmail}>
                    {resendSending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {t('auth:resendConfirmation')}
                  </Button>
                </form>
                <Button
                  onClick={() => navigate('/auth')}
                  variant="link"
                  className="w-full text-xs underline"
                >
                  {t('auth:goToLogin')}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="absolute top-4 right-4">
        <LanguageSelector />
      </div>
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-2" />
          <CardTitle>{t('auth:emailConfirmed')}</CardTitle>
          <CardDescription>{t('auth:emailConfirmedDesc')}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            className="w-full"
            onClick={() => navigate('/dashboard')}
          >
            {t('auth:goToDashboard')}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
